import React from 'react';
import { Drawer, Button, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RevenueTargetDrawerRow } from './types';
import { formatCurrency, formatExactCurrency, formatExactNumber } from './utils';
import { REVENUE_TARGET_DRAWER_PAGE_SIZE } from './constants';

type RevenueTargetCardData = {
  totalTarget: number;
  totalAchieved: number;
  totalRemaining: number;
  achievementPercent: number;
  highlightedState?: string;
  breakdown: RevenueTargetDrawerRow[];
};

interface RevenueTargetDrawerProps {
  open: boolean;
  onClose: () => void;
  cardData: RevenueTargetCardData | null;
  rows: RevenueTargetDrawerRow[];
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onViewCompanies: (row: RevenueTargetDrawerRow) => void;
  onViewReps: (row: RevenueTargetDrawerRow) => void;
  onCompanyLink: (event: React.MouseEvent<HTMLAnchorElement>, userid?: string) => void;
  formatTargetPercent: (value: number) => string;
}

type TableRow = RevenueTargetDrawerRow & {
  key: string;
};

const buildTableRows = (rows: RevenueTargetDrawerRow[]): TableRow[] =>
  rows.map((row) => ({
    ...row,
    key: `${row.state || 'unknown'}-${Math.round(row.target ?? 0)}-${Math.round(row.achieved ?? 0)}`,
  }));

const RevenueTargetDrawer: React.FC<RevenueTargetDrawerProps> = ({
  open,
  onClose,
  cardData,
  rows,
  totalPages,
  currentPage,
  onPageChange,
  onViewCompanies,
  onViewReps,
  onCompanyLink,
  formatTargetPercent,
}) => {
  const columns: ColumnsType<TableRow> = [
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (_value, record) => (
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-slate-900">{record.state || 'Unknown'}</span>
          <span className="text-xs text-slate-500">
            Top City:{' '}
            {record.topCity
              ? `${record.topCity}${record.topCityCount ? ` (${record.topCityCount})` : ''}`
              : '—'}
          </span>
        </div>
      ),
    },
    {
      title: 'Companies',
      dataIndex: 'companies',
      key: 'companies',
      render: (_value, record) => {
        const companies = record.companies ?? [];
        const visibleCompanies = companies.slice(0, 5);
        const remainingCompanies = companies.length - visibleCompanies.length;
        const companyCount = record.companyCount ?? companies.length;

        return (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-900">
              {formatExactNumber(companyCount)} {companyCount === 1 ? 'Company' : 'Companies'}
            </span>
            <div className="flex flex-wrap gap-2 text-xs text-slate-600">
              {visibleCompanies.map((company) => {
                const key = company.userid || company.name;
                if (company.userid) {
                  return (
                    <a
                      key={key}
                      href="#"
                      onClick={(event) => onCompanyLink(event, company.userid)}
                      className="text-blue-600 underline hover:text-blue-500"
                    >
                      {company.name}
                    </a>
                  );
                }
                return (
                  <span key={key} className="text-slate-500">
                    {company.name}
                  </span>
                );
              })}
              {remainingCompanies > 0 && (
                <span className="text-slate-500">+{remainingCompanies} more</span>
              )}
            </div>
            <Button
              type="link"
              size="small"
              className="!p-0 text-blue-600 w-fit"
              onClick={() => onViewCompanies(record)}
            >
              View companies
            </Button>
          </div>
        );
      },
    },
    {
      title: 'Sales Reps',
      dataIndex: 'reps',
      key: 'reps',
      render: (_value, record) => {
        const totalSalesReps = record.salesRepCount ?? record.reps?.length ?? 0;
        const topRepName = record.topSalesRepName;
        const topRepCityLabel = record.topSalesRepCity ? ` • ${record.topSalesRepCity}` : '';
        const topRepAccountsLabel = record.topSalesRepCompanyCount
          ? `${record.topSalesRepCompanyCount} active ${record.topSalesRepCompanyCount === 1 ? 'account' : 'accounts'}`
          : 'Account coverage: data unavailable';

        return (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-900">
              {formatExactNumber(totalSalesReps)} {totalSalesReps === 1 ? 'Sales Rep' : 'Sales Reps'}
            </span>
            <div className="flex flex-col gap-1 text-xs text-slate-600">
              <span>
                {topRepName
                  ? `Top performer: ${topRepName}${topRepCityLabel}`
                  : 'Top performer: —'}
              </span>
              <span>{topRepAccountsLabel}</span>
            </div>
            <Button
              type="link"
              size="small"
              className="!p-0 text-blue-600 w-fit"
              onClick={() => onViewReps(record)}
            >
              View sales reps
            </Button>
          </div>
        );
      },
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
      render: (value: number) => (
        <span className="text-sm font-semibold text-indigo-800" title={formatExactCurrency(Number(value ?? 0))}>
          {formatCurrency(Number(value ?? 0))}
        </span>
      ),
    },
    {
      title: 'Achieved',
      dataIndex: 'achieved',
      key: 'achieved',
      render: (value: number) => (
        <span className="text-sm font-semibold text-emerald-700" title={formatExactCurrency(Number(value ?? 0))}>
          {formatCurrency(Number(value ?? 0))}
        </span>
      ),
    },
    {
      title: 'Progress',
      dataIndex: 'percent',
      key: 'percent',
      align: 'right',
      render: (value: number) => (
        <Tag className="border-none bg-slate-100 text-sm font-semibold text-slate-700">
          {formatTargetPercent(Number(value ?? 0))}
        </Tag>
      ),
    },
  ];

  return (
    <Drawer
      title="State Revenue Target Breakdown"
      placement="right"
      width={1160}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
    >
      <div className="flex flex-col gap-4 bg-white p-6">
        {!cardData || cardData.breakdown.length === 0 ? (
          <p className="text-sm text-slate-500">
            No state-level target data available for the current filters.
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-blue-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Total Target</p>
                <p className="text-lg font-bold text-blue-700" title={formatExactCurrency(cardData.totalTarget)}>
                  {formatCurrency(cardData.totalTarget)}
                </p>
              </div>
              <div className="rounded-lg bg-emerald-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Total Achieved</p>
                <p className="text-lg font-bold text-emerald-700" title={formatExactCurrency(cardData.totalAchieved)}>
                  {formatCurrency(cardData.totalAchieved)}
                </p>
              </div>
            </div>

            <Table<TableRow>
              columns={columns}
              dataSource={buildTableRows(rows)}
              pagination={{
                current: currentPage,
                total: totalPages * REVENUE_TARGET_DRAWER_PAGE_SIZE,
                pageSize: REVENUE_TARGET_DRAWER_PAGE_SIZE,
                onChange: onPageChange,
                size: 'small',
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                className: '!px-4'
              }}
              rowKey={(record) => record.key}
              className="revenue-target-drawer-table border"
              size='small'
              scroll={{ x: true }}
            />
          </>
        )}
      </div>
    </Drawer>
  );
};

export default RevenueTargetDrawer;
